import React, { useMemo, useEffect } from 'react'
import { GlobalState } from '@leverege/ui-redux'
import { Text, DateTimeRangePicker } from '@leverege/ui-elements'
import { useSelector } from 'react-redux'
import moment from 'moment'
import S from '../components/Tables.css'
import AvgHeadCountOpts from './AvgHeadCountOpts'
import Model from '../../events/EventsViewModel'

const useHeadCount = ( props ) => {
  const { 
    item, 
    delegate,
    result,
    context,
  } = props
  const deviceId = item?.id || 'shellDevice'

  const queryName = `headCountAvg.${deviceId}`

  const settings = context.clientProps.settings
  const range = useMemo( () => {
    const initialRange = Model.getRange( settings )
    return DateTimeRangePicker.getRange( initialRange )
  }, [ settings ] )

  const avgData = useSelector( state => delegate.eventsDelegate.getList( state, queryName, { getAll : true } ) )

  useEffect( () => {
    GlobalState.dispatch(
      delegate.eventsDelegate.search( {
        query : { start : range.start, end : range.end, ...AvgHeadCountOpts },
        queryName
      } )
    )
  }, [ range ] )

  const avgProcessedData = useMemo( () => {
    if ( !avgData || !avgData.items ) return { directLabor : [], indirectLabor : [] }

    return avgData.items.reduce(
      ( acc, item ) => {
        const avgGrayWhiteTydexSum = 
          ( parseFloat( item.avgGraySum ) || 0 ) + 
          ( parseFloat( item.avgWhiteSum ) || 0 ) + 
          ( parseFloat( item.avgTydexSum ) || 0 )

        const avgMaroonRedSum = 
          ( parseFloat( item.avgMaroonSum ) || 0 ) + 
          ( parseFloat( item.avgRedSum ) || 0 )

        acc.directLabor.push( {
          grayWhiteTydex : avgGrayWhiteTydexSum.toFixed( 4 ),
          lightBlue : ( parseFloat( item.avgLightBlueSum ) ).toFixed( 4 ) || 0,
          mango : ( parseFloat( item.avgMangoSum ) ).toFixed( 4 ) || 0,
          orange : ( parseFloat( item.avgOrangeSum ) ).toFixed( 4 ) || 0,
        } )

        acc.indirectLabor.push( {
          darkBlue : ( parseFloat( item.avgDarkBlueSum ) ).toFixed( 4 ) || 0,
          maroonRed : avgMaroonRedSum.toFixed( 4 ),
          green : ( parseFloat( item.avgGreenSum ) ).toFixed( 4 ) || 0,
          dark : ( parseFloat( item.avgDarkSum ) ).toFixed( 4 ) || 0,
        } )

        return acc
      },
      { directLabor : [], indirectLabor : [] }
    )
  }, [ avgData ] )

  const processedData = useMemo( () => {
    if ( !result || !result.items ) return []

    return result.items.map( ( item ) => {
      const grayWhiteTydexSum = ( item.graySum || 0 ) + ( item.whiteSum || 0 ) + ( item.tydexSum || 0 )
      const maroonRedSum = ( item.maroonSum || 0 ) + ( item.redSum || 0 )

      return {
        time : item.time ? moment( item.time ).format( 'YYYY-MM-DD HH:mm' ) : '-',
        directHeadCountSum : item.directHeadCountSum,
        indirectHeadCountSum : item.indirectHeadCountSum,
        grayWhiteTydexSum,
        maroonRedSum,
        darkSum : item.darkSum,
        darkBlueSum : item.darkBlueSum,
        greenSum : item.greenSum,
        lightBlueSum : item.lightBlueSum,
        mangoSum : item.mangoSum,
        orangeSum : item.orangeSum,
      }
    } )
  }, [ result ] )

  const columns = [
    {
      id : 'time',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Time
        </Text>
      ),
      accessor : item => item.time,
    },
    {
      id : 'directCounts',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Direct Counts
        </Text>
      ),
      accessor : item => item.directHeadCountSum,
    },
    {
      id : 'indirectCounts',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Indirect Counts
        </Text>
      ),
      accessor : item => item.indirectHeadCountSum,
    },
    {
      id : 'grayWhiteTydex',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Gray/White/Tydex
        </Text>
      ),
      accessor : item => item.grayWhiteTydexSum,
    },
    {
      id : 'maroonRed',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Maroon/Red
        </Text>
      ),
      accessor : item => item.maroonRedSum,
    },
    {
      id : 'dark',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Dark
        </Text>
      ),
      accessor : item => item.darkSum,
    },
    {
      id : 'darkBlue',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Dark Blue
        </Text>
      ),
      accessor : item => item.darkBlueSum,
    },
    {
      id : 'green',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Green
        </Text>
      ),
      accessor : item => item.greenSum,
    },
    {
      id : 'lightBlue',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Light Blue
        </Text>
      ),
      accessor : item => item.lightBlueSum,
    },
    {
      id : 'mango',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Mango
        </Text>
      ),
      accessor : item => item.mangoSum,
    },
    {
      id : 'orange',
      Header : (
        <Text className={S.ellipsizeLeft} variant="columnHeaderCustom">
          Orange
        </Text>
      ),
      accessor : item => item.orangeSum,
    }
  ]

  const directLaborFields = [
    { name : 'Gray/White/Tydex', accessor : 'grayWhiteTydex' },
    { name : 'Light Blue', accessor : 'lightBlue' },
    { name : 'Mango', accessor : 'mango' },
    { name : 'Orange', accessor : 'orange' },
  ]

  const indirectLaborFields = [
    { name : 'Dark Blue', accessor : 'darkBlue' },
    { name : 'Maroon/Red', accessor : 'maroonRed' },
    { name : 'Green', accessor : 'green' },
    { name : 'Dark', accessor : 'dark' },
  ]

  return {
    columns,
    directLaborFields,
    indirectLaborFields,
    data : processedData,
    avgData : avgProcessedData,
  }
}

export default useHeadCount
